import { queryClient } from "@/api/constants"
import { StyleReferenceResponseDTO } from "@/api/dto/style-references"
import { CreateStyleRequestDTO, StyleResponseDTO, UpdateStyleRequestDTO } from "@/api/dto/styles"
import { PNApiOperations } from "@/api/operations"
import { Adjustments } from "@/core/types"
// eslint-disable-next-line no-restricted-imports -- store-layer i18n
import i18n from "@/i18n"
import { AxiosError } from "axios"
import { StyleInfoRecord, StyleReferenceRecord } from "@/lib/catalog"
import {
	fetchCurrentPreview,
	fetchOriginalPreview,
	fetchPreviewWithTags,
} from "@/storage/indexeddb"
import { PreviewSize } from "../previews/previews-types"
import { cullingImporter } from "../importing/culling-importing"
import { photosAtom, getPhotoMetadata } from "../photo/photos-state"
import { store } from "../store"
import { resizeImageToSize } from "@/core/utils/resize-image"
import { updatePreviews } from "../previews/previews-state"
import { ListingResponseDTO } from "@/api/dto/shared"
import { InfiniteData } from "@tanstack/query-core"
import { renderPreview } from "../previews/previews-processing"
import { getFileForPhoto } from "../files-state"
import { STYLE_COVER_LONGEST_SIDE, STYLE_COVER_OUTPUT_QUALITY } from "./style-cover-constants"
import { NetworkRequestIntent } from "@/api/network-behavior"
import {
	cacheReferenceWebpData256x256,
	createImageInfoWithoutReferenceWebpData,
	hasReferenceWebpData256x256Source,
} from "@/lib/style/reference-webp-data"
import { toRemoteStyleReferenceImageId } from "./style-reference-id"

export class DuplicateStyleNameError extends Error {
	readonly styleName: string
	readonly cause?: unknown

	constructor(styleName: string, cause?: unknown) {
		super(`A style named "${styleName}" already exists.`)
		this.name = "DuplicateStyleNameError"
		this.styleName = styleName
		this.cause = cause
	}
}

export const isDuplicateStyleNameError = (error: unknown): error is DuplicateStyleNameError => {
	return error instanceof DuplicateStyleNameError
}

export const getDuplicateStyleNameErrorMessage = (styleName: string) => {
	return i18n.t("store:styleService.duplicateStyleNameMessage", { styleName })
}

export class StyleService {
	private static isUploadableBlob(blob: unknown): blob is Blob {
		return blob instanceof Blob && blob.size > 0
	}

	private static getUploadContentType(blob: Blob): string {
		return blob.type || "application/octet-stream"
	}

	private static referenceWebpDataToBlob(webpData: Uint8Array): Blob {
		const data = new Uint8Array(webpData)
		return new Blob([data], {
			type: "image/webp",
		})
	}

	static async getReferencesForStyle(styleId: string): Promise<StyleReferenceResponseDTO[]> {
		return (await PNApiOperations.getStyleReferences(styleId)).data
	}

	static async uploadStyleReferenceImage(imageData: Blob) {
		if (!StyleService.isUploadableBlob(imageData)) {
			throw new Error("Cannot upload empty image data")
		}
		const uploadUrls = await PNApiOperations.getSignedUrls([
			{
				contentType: "image",
				entityType: "reference-image",
				contentDigest: undefined,
			},
		])

		const uploadUrl = uploadUrls[0]
		if (!uploadUrl) {
			throw new Error("Couldn't get upload url for reference image")
		}

		const { url } = await PNApiOperations.uploadContent({
			contentType: StyleService.getUploadContentType(imageData),
			data: imageData,
			url: uploadUrl.url,
		})

		return url
	}

	static async uploadStyleReferenceImages(imageData: Blob[]) {
		if (!imageData.every((blob) => StyleService.isUploadableBlob(blob))) {
			throw new Error("Cannot upload invalid style reference image data")
		}

		const uploadUrls = await PNApiOperations.getSignedUrls(
			imageData.map(() => ({
				contentType: "image",
				entityType: "reference-image",
				contentDigest: undefined,
			})),
		)

		return Promise.allSettled(
			imageData.map(async (imageBlob, index) => {
				const uploadUrl = uploadUrls[index]

				if (!uploadUrl) {
					throw new Error("Couldn't match thumbnail with upload url")
				}

				const { url } = await PNApiOperations.uploadContent({
					contentType: StyleService.getUploadContentType(imageBlob),
					data: imageBlob,
					url: uploadUrl.url,
				})

				return url
			}),
		)
	}

	static async deleteStyleReference(styleId: string, imageId: string) {
		return await PNApiOperations.deleteStyleReference(styleId, imageId)
	}

	static async deleteStyleReferences(styleId: string, styleVersion: number, imageIds: string[]) {
		if (imageIds.length > 0) {
			// TODO: implement batch deletion
			for (const id of imageIds) {
				await PNApiOperations.deleteStyleReference(styleId, id)
			}
			await queryClient.invalidateQueries({ queryKey: ["style_references", styleId] })
			await queryClient.invalidateQueries({ queryKey: ["user_styles"] })
			await StyleService.updateStyle({
				styleId,
				version: styleVersion + 1, //increment version when references are deleted
			})
		}
	}

	static async fetchStyle(
		styleId: string,
		networkIntent: NetworkRequestIntent = "background",
	): Promise<StyleResponseDTO> {
		return PNApiOperations.getStyle(styleId, networkIntent)
	}

	static async createStyle(
		styleName: string,
		presets: Record<string, Partial<Adjustments>>,
		lutUrls?: Record<string, string>,
	) {
		const currentDate = new Date()
		const createStyleDTO: CreateStyleRequestDTO = {
			name: styleName,
			createdAt: currentDate,
			updatedAt: currentDate,
			schemaVersion: 0,
			presets: presets,
			lutUrls: lutUrls,
			beforeAfterPhotos: [],
			version: 0,
		}

		try {
			const response = await PNApiOperations.createStyle(createStyleDTO)
			void queryClient.invalidateQueries({ queryKey: ["user_styles"] })
			return response
		} catch (error) {
			if (error instanceof AxiosError && error.response?.status === 409) {
				throw new DuplicateStyleNameError(styleName, error)
			}

			throw error
		}
	}

	static async updateStyle(
		updatedData: UpdateStyleRequestDTO,
		loadingPlaceholder?: Partial<StyleResponseDTO>,
	) {
		const request: UpdateStyleRequestDTO = {
			...updatedData,
		}
		try {
			const response = await PNApiOperations.updateStyle(request)
			if (loadingPlaceholder) {
				queryClient.setQueryData<
					InfiniteData<ListingResponseDTO<StyleResponseDTO>, string | undefined>
				>(["user_styles"], (prev) => {
					if (!prev) return prev
					return {
						...prev,
						pages: prev.pages.map((page) => ({
							...page,
							data: page.data.map((s) =>
								s.styleId === response.styleId
									? {
											...response,
											...loadingPlaceholder,
										}
									: s,
							),
						})),
						pageParams: prev.pageParams,
					}
				})
			}

			void queryClient.invalidateQueries({ queryKey: ["user_styles"] })
			return response
		} catch (error) {
			if (request.name && error instanceof AxiosError && error.response?.status === 409) {
				throw new DuplicateStyleNameError(request.name, error)
			}

			throw error
		}
	}

	static async deleteStyle(id: string) {
		return PNApiOperations.deleteStyle(id).then((response) => {
			void queryClient.invalidateQueries({ queryKey: ["user_styles"] })
			return response
		})
	}

	static async duplicateStyle(styleId: string, newStyleName: string) {
		try {
			const response = await PNApiOperations.duplicateStyle({
				styleId: styleId,
				newName: newStyleName,
			})
			void queryClient.invalidateQueries({ queryKey: ["user_styles"] })
			return response
		} catch (error) {
			if (error instanceof AxiosError && error.response?.status === 409) {
				throw new DuplicateStyleNameError(newStyleName, error)
			}

			throw error
		}
	}

	static async uploadCoverImage(photoId: string, projectId: string) {
		const photo = store.get(photosAtom)[photoId]
		if (photo) {
			const file = getFileForPhoto(photo)
			if (file) {
				const renderedCover = await renderPreview(file, photo, {
					resizeWidth: STYLE_COVER_LONGEST_SIDE,
					resizeHeight: STYLE_COVER_LONGEST_SIDE,
					quality: STYLE_COVER_OUTPUT_QUALITY,
				})
				if (renderedCover) {
					return StyleService.uploadStyleReferenceImage(renderedCover)
				}
			}
		}

		const preview = await fetchPreviewWithTags({
			photoId,
			projectId,
			size: PreviewSize.medium,
			tags: ["edited"],
		})
		if (!preview) {
			return
		}

		const resizedCover = await resizeImageToSize({
			image: preview.data,
			longestSide: STYLE_COVER_LONGEST_SIDE,
			options: { outputQuality: STYLE_COVER_OUTPUT_QUALITY },
		})
		return StyleService.uploadStyleReferenceImage(resizedCover)
	}

	static async uploadReference(
		localReference: StyleReferenceRecord,
		styleId: string,
		projectId?: string,
	) {
		const localImageId = localReference.imageInfo.imageId
		//If there are existing before or after urls, it means they were uploaded and they have not changed so we can reuse
		let afterImageUrl = localReference.afterImageUrl
		let beforeImageUrl = localReference.beforeImageUrl

		// Prepare both previews first (these are fast, can be sequential)
		let afterPreview: Blob | undefined = undefined
		let beforePreview: Blob | undefined = undefined

		if (!afterImageUrl) {
			//Use provided image data if available
			if (StyleService.isUploadableBlob(localReference.afterImageData)) {
				afterPreview = localReference.afterImageData
			} else if (localReference.afterImageData) {
				console.warn("Ignoring invalid afterImageData for style reference upload", {
					imageId: localImageId,
				})
			}

			//Try fetching from indexeddb preview if its a photo within this project
			const photo = store.get(photosAtom)[localImageId]
			if (!StyleService.isUploadableBlob(afterPreview) && projectId && photo) {
				afterPreview = (
					await fetchCurrentPreview({
						projectId: projectId,
						photo: photo,
						size: PreviewSize.small,
					})
				)?.data
			}

			//Fall back to the embedded reference image data if available
			if (
				!StyleService.isUploadableBlob(afterPreview) &&
				localReference.imageInfo.referenceWebpData256x256
			) {
				// Create a new Uint8Array to ensure we have a regular ArrayBuffer
				const blob = StyleService.referenceWebpDataToBlob(
					localReference.imageInfo.referenceWebpData256x256,
				)
				const size = localReference.imageInfo.features.imageSize
				const resized = await resizeImageToSize({
					image: blob,
					options: { outputQuality: 1.0 },
					longestSide: 256,
					originalSize: size,
				})
				afterPreview = resized
			}
		}

		if (!beforeImageUrl) {
			if (StyleService.isUploadableBlob(localReference.beforeImageData)) {
				beforePreview = localReference.beforeImageData
			} else if (localReference.beforeImageData) {
				console.warn("Ignoring invalid beforeImageData for style reference upload", {
					imageId: localImageId,
				})
			}
			if (!StyleService.isUploadableBlob(beforePreview) && projectId) {
				//try fetching from indexeddb first
				beforePreview = (
					await fetchOriginalPreview({
						photoId: localImageId,
						projectId,
						size: PreviewSize.small,
					})
				)?.data

				//extract from metadata if beforePreview is not found
				if (!StyleService.isUploadableBlob(beforePreview)) {
					const photoId = localImageId
					const targetPhoto = store.get(photosAtom)[photoId]
					const metadata = getPhotoMetadata(photoId)
					if (metadata && targetPhoto) {
						beforePreview = await cullingImporter.getOrExtractPreviewDataForCulling({
							photo: targetPhoto,
							metadata,
						})
					}

					// Extracted preview is medium size, resize to 512 if needed and store as small size
					if (StyleService.isUploadableBlob(beforePreview)) {
						beforePreview = await resizeImageToSize({
							image: beforePreview,
							options: { outputQuality: 0.5 },
							longestSide: 512,
						})

						void updatePreviews({
							previewsInfo: [
								{
									projectId: projectId,
									photoId: photoId,
									data: beforePreview,
									size: PreviewSize.small,
									tags: ["extracted", "original"],
								},
							],
						})
					}
				}
			}
		}

		// Upload both images in parallel, batching signed URL requests
		const imagesToUpload: Blob[] = []
		const urlIndices: {
			type: "after" | "before" | "referenceWebpData256x256"
			index: number
		}[] = []
		let referenceWebpData256x256Url = localReference.imageInfo.referenceWebpData256x256Url

		if (!afterImageUrl && StyleService.isUploadableBlob(afterPreview)) {
			urlIndices.push({ type: "after", index: imagesToUpload.length })
			imagesToUpload.push(afterPreview)
		}

		if (!beforeImageUrl && StyleService.isUploadableBlob(beforePreview)) {
			urlIndices.push({ type: "before", index: imagesToUpload.length })
			imagesToUpload.push(beforePreview)
		}

		if (
			!referenceWebpData256x256Url &&
			localReference.imageInfo.referenceWebpData256x256?.length
		) {
			urlIndices.push({
				type: "referenceWebpData256x256",
				index: imagesToUpload.length,
			})
			imagesToUpload.push(
				StyleService.referenceWebpDataToBlob(
					localReference.imageInfo.referenceWebpData256x256,
				),
			)
		}

		if (imagesToUpload.length > 0) {
			const results = await StyleService.uploadStyleReferenceImages(imagesToUpload)
			for (const { type, index } of urlIndices) {
				const result = results[index]
				if (!result) {
					throw new Error(`No result for ${type} image at index ${index}`)
				}
				if (result.status === "fulfilled") {
					if (type === "after") {
						afterImageUrl = result.value
					} else if (type === "before") {
						beforeImageUrl = result.value
					} else {
						referenceWebpData256x256Url = result.value
					}
				} else {
					throw new Error(`Failed to upload ${type} image: ${result.reason}`)
				}
			}
		}

		if (!afterImageUrl) {
			console.warn("No after image url for style reference")
		}

		if (referenceWebpData256x256Url) {
			localReference.imageInfo.referenceWebpData256x256Url = referenceWebpData256x256Url
			if (localReference.imageInfo.referenceWebpData256x256?.length) {
				cacheReferenceWebpData256x256(
					referenceWebpData256x256Url,
					localReference.imageInfo.referenceWebpData256x256,
				)
			}
		}

		if (
			localReference.imageInfo.colorMatchReference &&
			!hasReferenceWebpData256x256Source(localReference.imageInfo)
		) {
			console.warn("No referenceWebpData256x256 source for style reference", {
				imageId: localImageId,
			})
		}

		// Binary fields (sceneClusterFeature, faceColors, faceWeights)
		// are automatically serialized by the API operations layer
		const remoteImageId = toRemoteStyleReferenceImageId(localImageId)
		// Keep the reference key encoded while preserving the local id inside imageInfo.
		const uploaded = await PNApiOperations.createOrUpdateStyleReference(styleId, {
			imageId: remoteImageId,
			imageInfo: createImageInfoWithoutReferenceWebpData({
				...localReference.imageInfo,
				imageId: localImageId,
			}),
			beforeImageUrl: beforeImageUrl,
			afterImageUrl: afterImageUrl ?? undefined,
			isImplicit: localReference.isImplicit ?? false,
		})
		return StyleService.responseToReference(uploaded)
	}

	static responseToReference(response: StyleReferenceResponseDTO): StyleReferenceRecord {
		// Binary fields are automatically deserialized by the API operations layer
		return {
			referenceId: response.referenceId,
			styleId: response.styleId,
			imageInfo: response.imageInfo,
			imageId: response.imageId,
			beforeImageUrl: response.beforeImageUrl,
			afterImageUrl: response.afterImageUrl,
			isImplicit: response.isImplicit,
		}
	}

	static responseToStyleInfo(
		response: StyleResponseDTO,
		prioritizeImageIds?: Array<string>,
		globalBiases?: Partial<Adjustments>,
	): StyleInfoRecord {
		return {
			styleId: response.styleId,
			name: response.name,
			description: response.description,
			author: response.author,
			categories: response.categories,
			createdAt: response.createdAt,
			updatedAt: response.updatedAt,
			referenceCount: response.referenceCount,
			schemaVersion: response.schemaVersion,
			presets: response.presets,
			lutUrls: response.lutUrls,
			coverImageUrl: response.coverImageUrl,
			prioritizeImageIds: prioritizeImageIds ?? [],
			visibility: response.visibility ?? "personal",
			version: response.version ?? 0,
			globalBiases: globalBiases,
		}
	}
}
